// API Configuration
// This file contains all API endpoints and configuration settings
// Update the BASE_URL to change the backend service URL

const API_CONFIG = {
  // Backend service base URL - can be overridden by environment variable
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://voicefuncappv21150.azurewebsites.net',
  
  // API endpoints
  ENDPOINTS: {
    // Soniox API endpoints
    SONIOX_TEMP_KEY: '/api/soniox/temp-key',
    
    // Add other endpoints here as needed
    // EXAMPLE: USER_PROFILE: '/api/user/profile',
    // EXAMPLE: UPLOAD_FILE: '/api/upload',
  },
  
  // Request configuration
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    // Add ngrok-skip-browser-warning header for ngrok URLs
    'ngrok-skip-browser-warning': 'true',
  },
  
  // Performance optimization: Fine-tuned timeout settings as per server optimization
  TIMEOUT: {
    DEFAULT: 5000,  // 5 seconds (reduced from 10s for better responsiveness)
    CONNECT: 5000,  // 5 seconds connection timeout
    READ: 5000,     // 5 seconds read timeout
    WRITE: 5000,    // 5 seconds write timeout
    POOL: 5000,     // 5 seconds pool timeout
    UPLOAD: 30000,  // 30 seconds for file uploads
    TRANSCRIPTION: 60000, // 60 seconds for transcription requests
  }
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, ''); // Remove trailing slash
  const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${endpointPath}`;
};

// Helper function to get API endpoint URL
export const getApiEndpoint = (endpointKey) => {
  const endpoint = API_CONFIG.ENDPOINTS[endpointKey];
  if (!endpoint) {
    throw new Error(`API endpoint '${endpointKey}' not found in configuration`);
  }
  return buildApiUrl(endpoint);
};

// Performance optimization: Enhanced API request function with timeout and monitoring
export const apiRequest = async (url, options = {}) => {
  const startTime = performance.now();

  const defaultOptions = {
    method: options.method || 'GET',
    headers: {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...options.headers,
    },
    mode: 'cors', // Enable CORS
    credentials: 'omit', // Don't send credentials for cross-origin requests
    ...options,
  };

  // Performance optimization: Implement timeout using AbortController
  const { timeout = API_CONFIG.TIMEOUT.DEFAULT, ...fetchOptions } = defaultOptions;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  fetchOptions.signal = options.signal || controller.signal;

  try {
    // Performance optimization: Reduced logging as per server optimization
    if (process.env.NODE_ENV === 'development' && url.includes('/api/')) {
      console.log(`API Request: ${fetchOptions.method} ${new URL(url).pathname}`);
    }

    const response = await fetch(url, fetchOptions);
    const duration = performance.now() - startTime;

    // Performance optimization: Performance monitoring with reduced logging
    if (process.env.NODE_ENV === 'development' && url.includes('/api/')) {
      console.log(`API Response: ${response.status} (${duration.toFixed(1)}ms)`);
    }

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Performance optimization: Enhanced error handling
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }

    // Performance optimization: Reduced error logging
    if (process.env.NODE_ENV === 'development') {
      console.error('API request failed:', error.message);
      console.error('URL:', url);
    }
    throw error;
  }
};

// Export configuration for direct access if needed
export default API_CONFIG;

// Environment-specific configuration is handled in the BASE_URL definition above

// Performance optimization: Reduced development mode logging
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration loaded:', {
    baseUrl: API_CONFIG.BASE_URL,
    timeouts: API_CONFIG.TIMEOUT,
  });
}
